// Confirmação de ações destrutivas com <dialog>, no lugar do confirm() nativo.
//
// O confirm() do navegador não respeita o tema, não é estilizável e quebra a
// linguagem visual justamente no momento mais crítico. Turbo Drive está
// desligado neste projeto (ver js/turbo.js), então `data-turbo-confirm` não
// se aplica — daí esta implementação própria.
//
// Uso (via helper `confirm_button`, ou direto):
//   <form data-confirm-dialog="Remover este membro?" data-confirm-label="Remover">
//
// Em formulários com vários submits, os atributos podem ir no botão — só o
// botão destrutivo confirma. A string "%{count}" é trocada pela quantidade de
// checkboxes `ids[]` marcados (ações em massa).

const TONES = {
  error: { btn: "btn-error", icon: "lucide--triangle-alert", wrap: "bg-error/10 text-error" },
  warning: { btn: "btn-warning", icon: "lucide--circle-alert", wrap: "bg-warning/10 text-warning" },
  primary: { btn: "btn-primary", icon: "lucide--circle-help", wrap: "bg-primary/10 text-primary" },
}

let dialog

function buildDialog() {
  const el = document.createElement("dialog")
  el.className = "modal"
  el.innerHTML = `
    <div class="modal-box max-w-sm">
      <div class="flex gap-4">
        <div data-role="icon-wrap" class="rounded-box flex size-10 shrink-0 items-center justify-center">
          <span data-role="icon" class="iconify size-5" aria-hidden="true"></span>
        </div>
        <div class="min-w-0">
          <h2 class="text-base font-semibold">Confirmar ação</h2>
          <p data-role="message" class="text-base-content/70 mt-1 text-sm"></p>
        </div>
      </div>
      <div class="modal-action mt-6">
        <button type="button" class="btn btn-sm btn-ghost" data-role="cancel">Cancelar</button>
        <button type="button" class="btn btn-sm" data-role="confirm"></button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop"><button aria-label="Fechar">close</button></form>
  `
  document.body.appendChild(el)
  return el
}

function ask({ message, label, tone }) {
  dialog ||= buildDialog()

  const t = TONES[tone] || TONES.error
  const confirmBtn = dialog.querySelector("[data-role=confirm]")
  const cancelBtn = dialog.querySelector("[data-role=cancel]")
  const iconWrap = dialog.querySelector("[data-role=icon-wrap]")
  const icon = dialog.querySelector("[data-role=icon]")

  dialog.querySelector("[data-role=message]").textContent = message
  confirmBtn.textContent = label
  confirmBtn.className = `btn btn-sm ${t.btn}`
  iconWrap.className = `rounded-box flex size-10 shrink-0 items-center justify-center ${t.wrap}`
  icon.className = `iconify ${t.icon} size-5`

  return new Promise((resolve) => {
    const done = (value) => {
      confirmBtn.removeEventListener("click", onConfirm)
      resolve(value)
    }
    const onConfirm = () => {
      dialog.close()
      done(true)
    }

    confirmBtn.addEventListener("click", onConfirm)
    dialog.addEventListener("close", () => done(false), { once: true })
    cancelBtn.onclick = () => dialog.close()

    dialog.showModal()
    // Foco no cancelar: a ação destrutiva não deve ser confirmável com Enter
    // logo após o diálogo abrir.
    cancelBtn.focus()
  })
}

// Intercepta o submit na fase de captura, para rodar antes de qualquer outro
// handler do formulário.
document.addEventListener(
  "submit",
  (event) => {
    const form = event.target
    if (!form || form.dataset.confirmed === "true") return

    const submitter = event.submitter
    const source = submitter?.dataset?.confirmDialog ? submitter : form
    const message = source.dataset?.confirmDialog
    if (!message) return

    event.preventDefault()
    event.stopPropagation()

    const count = form.querySelectorAll("input[name='ids[]']:checked").length

    ask({
      message: message.replace("%{count}", count),
      label: source.dataset.confirmLabel || "Confirmar",
      tone: source.dataset.confirmTone || "error",
    }).then((ok) => {
      if (!ok) return
      form.dataset.confirmed = "true"
      // Repassa o submitter para preservar o name/value do botão (ex.: bulk_action).
      form.requestSubmit(submitter || undefined)
    })
  },
  true,
)
